/**
 * AstorBriefing — Borrower Home hero card.
 *
 * Concierge-style AI digest at the top of the borrower Home page. Wraps the
 * shared `AstorBriefingCard` primitive from `23-design-system-ui` and feeds
 * it real backend data from `trpc.astor.getBriefing` (deterministic
 * projection in `8-agent-service`; future LLM swap stays compatible with
 * this hook).
 *
 * Wiring contract:
 * - Headline + bullets come from `AstorBriefingDto` returned by the agent
 *   service. The borrower-level `tone` (MINT/AMBER/NEUTRAL) is mapped to
 *   the per-bullet tone the design-system card expects.
 * - Overnight prose is treated as a single block; the card renders it as a
 *   collapsible footer.
 * - Refresh invalidates the tRPC cache so the user can pull a fresh
 *   projection on demand. While loading we keep the previous render shown
 *   to avoid a layout shimmer; on first-load we render a neutral
 *   placeholder copy from i18n.
 */

import { AstorBriefingCard, type AstorBriefingCardProps, useI18n } from '@ops-matrix/design-system-ui';
import type { AstorBriefingTone } from '@ops-matrix/shared-types';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { trpc } from '../../lib/trpc';
import { useAstorBriefing } from '../../lib/trpc/hooks';

export interface AstorBriefingProps {
  /** The signed-in borrower's first name — used by the page header greeting, not the card itself. */
  firstName?: string;
}

/** Map the borrower-level briefing tone to the per-bullet tone tokens the card expects. */
const toneForBullet = (tone: AstorBriefingTone | undefined): 'mint' | 'amber' | 'id' => {
  if (tone === 'AMBER') return 'amber';
  if (tone === 'MINT') return 'mint';
  return 'id';
};

export function AstorBriefing(_props: AstorBriefingProps): React.ReactElement {
  const { t } = useI18n();
  const navigate = useNavigate();
  const utils = trpc.useUtils();

  const briefingQuery = useAstorBriefing();

  const openAstor = useCallback(() => navigate('/chat'), [navigate]);

  const handleRefresh = useCallback(() => {
    void utils.astor.getBriefing.invalidate();
  }, [utils]);

  const headline = briefingQuery.data?.headline ?? t('consumer.home.briefing.placeholderHeadline');
  const tone = briefingQuery.data?.tone;

  const bullets = useMemo<AstorBriefingCardProps['bullets']>(() => {
    const rows = briefingQuery.data?.bullets ?? [];
    if (rows.length === 0) {
      // First-load / empty-state — keep the card usable with the existing
      // placeholder copy so the dashboard doesn't show a blank hero.
      return [];
    }
    return rows.map((b) => ({
      id: b.id,
      tone: toneForBullet(tone),
      text: b.label,
      action: b.href
        ? { label: t('consumer.home.briefing.openAstor'), onClick: () => navigate(b.href as string) }
        : { label: t('consumer.home.briefing.openAstor'), onClick: openAstor },
    }));
  }, [briefingQuery.data?.bullets, tone, t, navigate, openAstor]);

  const overnight = useMemo<string[]>(() => {
    const text = briefingQuery.data?.overnight?.trim();
    return text ? [text] : [];
  }, [briefingQuery.data?.overnight]);

  const timestampLabel = useMemo(() => {
    const iso = briefingQuery.data?.generatedAt;
    const at = iso ? new Date(iso) : new Date();
    return t('consumer.home.briefing.timestamp', {
      time: at.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }),
    });
  }, [briefingQuery.data?.generatedAt, t]);

  return (
    <AstorBriefingCard
      eyebrow={t('consumer.home.briefing.label')}
      headline={headline}
      bullets={bullets}
      overnight={overnight}
      timestamp={timestampLabel}
      onRefresh={handleRefresh}
      openAstorLabel={t('consumer.home.briefing.openAstor')}
      onOpenAstor={openAstor}
      showOvernightLabel={(count) => t('consumer.home.briefing.showOvernightCount', { count })}
      hideOvernightLabel={(count) => t('consumer.home.briefing.hideOvernightCount', { count })}
    />
  );
}
